import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Fade,
  Slide,
  Stack,
  useTheme,
  useMediaQuery,
  MenuItem,
  Alert,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import chatsApi from '../api/chatsApi';
import knowledgeApi from '../api/knowledgeApi';

const CreateChatDialog = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [chatName, setChatName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState('');
  const [isLoadingKb, setIsLoadingKb] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadKnowledgeBases = async () => {
      try {
        const kbList = await knowledgeApi.getAll();
        setKnowledgeBases(kbList);
      } catch (error) {
        console.error('Failed to load knowledge bases:', error);
        setError('Failed to load knowledge bases');
      } finally {
        setIsLoadingKb(false);
      }
    };
    loadKnowledgeBases();
  }, []);

  const handleCreateChat = async () => {
    if (!chatName.trim()) {
      setError('Chatbot name is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const createdChat = await chatsApi.create({
        name: chatName,
        description,
        knowledgeBaseId: selectedKnowledgeBaseId || null,
      });
      
      navigate(`/dashboard/chats/${createdChat.recId}`);
    } catch (error) {
      console.error('Chat creation failed:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create chatbot');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <Slide in direction="up" timeout={500}>
          <Box
            sx={{
              width: isMobile ? '90%' : '500px',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '16px',
              p: 3,
              boxShadow: theme.shadows[5],
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                }}
              >
                Create Chatbot
              </Typography>
              <IconButton
                onClick={() => navigate('/dashboard/chats')}
                sx={{ color: theme.palette.text.secondary }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Form Content */}
            <Stack spacing={2}>
              {/* Chatbot Name */}
              <TextField
                fullWidth
                label="Chatbot Name"
                variant="outlined"
                value={chatName}
                onChange={(e) => {
                  setChatName(e.target.value);
                  setError(null);
                }}
                required
                error={error && !chatName.trim()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />

              {/* Description Field */}
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              {/* Knowledge Base Dropdown */}
              <TextField
                select
                fullWidth
                label="Knowledge Base"
                value={selectedKnowledgeBaseId}
                onChange={(e) => setSelectedKnowledgeBaseId(e.target.value)}
                disabled={isLoadingKb}
                variant="outlined"
                SelectProps={{
                  MenuProps: {
                    style: { zIndex: 9999 },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        maxHeight: '200px',
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {knowledgeBases.map((kb) => (
                  <MenuItem key={kb.recId} value={kb.recId}>
                    {kb.Name}
                  </MenuItem>
                ))}
                {isLoadingKb && (
                  <MenuItem disabled>Loading knowledge bases...</MenuItem>
                )}
              </TextField>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard/chats')}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateChat}
                  disabled={isLoading || !chatName.trim()}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  {isLoading ? 'Creating...' : 'Create'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Slide>
      </Box>
    </Fade>
  );
};

export default CreateChatDialog;